import React, { useState } from "react";
import Swal from "sweetalert2";
import style from "../../../scss/Catalogos.module.scss";
import SelectComponent from "../../../components/SelectComponent";
import { obtenerMensajeError } from "../../../utils/mensajeError";
import { BotonEditar, BotonEliminar } from "../../../components/BotonIconoComponent";

// CRUD genérico para un catálogo de un campo principal (nombre de candidato o
// de lugar), con un "tipo" opcional (select) y una "cantidad" opcional (número,
// p. ej. las mesas de un lugar).
const CatalogoComponent = (props) => {
  const {
    titulo,
    etiquetaCampo,
    items,
    obtenerValor,
    opcionesTipo,
    obtenerTipo,
    etiquetaTipo,
    etiquetaCantidad,
    obtenerCantidad,
    formatoCantidad = (cantidad) => cantidad,
    // registros que no se pueden editar ni eliminar (opciones fijas)
    esFijo = () => false,
    onCrear,
    onEditar,
    onEliminar,
    onCambio,
  } = props;

  const tipoPorDefecto = opcionesTipo?.[0]?.value ?? "";
  const [valor, setValor] = useState("");
  const [tipo, setTipo] = useState(tipoPorDefecto);
  const [cantidad, setCantidad] = useState("");
  const [editandoId, setEditandoId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const idCampo = `campo-${titulo}`.replace(/\s+/g, "-").toLowerCase();

  const limpiarFormulario = () => {
    setValor("");
    setTipo(tipoPorDefecto);
    setCantidad("");
    setEditandoId(null);
  };

  const iniciarEdicion = (item) => {
    setEditandoId(item.id);
    setValor(String(obtenerValor(item)));
    // un registro sin tipo (null) arranca con el primero para que el select quede controlado
    if (opcionesTipo) setTipo(obtenerTipo(item) ?? tipoPorDefecto);
    if (etiquetaCantidad) setCantidad(String(obtenerCantidad(item) ?? ""));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const valorLimpio = valor.trim();
    if (!valorLimpio) return;
    const cantidadNumero = etiquetaCantidad ? Number(cantidad) : undefined;

    setGuardando(true);
    try {
      const respuesta = editandoId
        ? await onEditar(editandoId, valorLimpio, tipo, cantidadNumero)
        : await onCrear(valorLimpio, tipo, cantidadNumero);
      Swal.fire({ icon: "success", text: respuesta.msg, timer: 1800, showConfirmButton: false });
      limpiarFormulario();
      onCambio();
    } catch (error) {
      Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = (item) => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: `Se eliminará "${obtenerValor(item)}" de ${titulo.toLowerCase()}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, Confirmar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const respuesta = await onEliminar(item.id);
        Swal.fire({ icon: "success", text: respuesta.msg, timer: 1800, showConfirmButton: false });
        if (editandoId === item.id) limpiarFormulario();
        onCambio();
      } catch (error) {
        Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
      }
    });
  };

  return (
    <section className={style.tarjeta}>
      <h2 className={style.titulo}>
        {titulo} <span className={style.contador}>{items.length}</span>
      </h2>

      {/* La tarjeta puede ser angosta (1/3 de pantalla en desktop): los
          campos van apilados porque los breakpoints miden la pantalla, no la tarjeta */}
      <form onSubmit={handleSubmit} className="row g-2">
        <div className="col-12">
          <label htmlFor={idCampo} className={style.etiqueta}>
            {etiquetaCampo}
          </label>
          <input
            id={idCampo}
            type="text"
            className="form-control"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            required
          />
        </div>

        {etiquetaCantidad && (
          <div className="col-12">
            <label htmlFor={`${idCampo}-cantidad`} className={style.etiqueta}>
              {etiquetaCantidad}
            </label>
            <input
              id={`${idCampo}-cantidad`}
              type="number"
              inputMode="numeric"
              min={1}
              max={999}
              step={1}
              className="form-control"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              required
            />
          </div>
        )}

        {opcionesTipo && (
          <div className="col-12">
            <SelectComponent
              id={`${idCampo}-tipo`}
              etiqueta="Tipo"
              claseEtiqueta={style.etiqueta}
              opciones={opcionesTipo}
              valor={tipo}
              onCambiar={(valor) => setTipo(valor ?? tipoPorDefecto)}
              buscable={false}
              limpiable={false}
            />
          </div>
        )}

        <div className={`col-12 ${style.botones}`}>
          <button type="submit" className={`btn btn-primary ${style.boton}`} disabled={guardando}>
            {editandoId ? "Guardar cambios" : "Agregar"}
          </button>
          {editandoId && (
            <button type="button" className={`btn btn-outline-secondary ${style.boton}`} onClick={limpiarFormulario}>
              Cancelar
            </button>
          )}
        </div>
      </form>

      <div className={`table-responsive ${style.lista}`}>
        <table className="table table-hover table-striped align-middle mb-0">
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td className="text-center text-muted">No hay registros</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className={editandoId === item.id ? "table-primary" : ""}>
                  <td>{obtenerValor(item)}</td>
                  {opcionesTipo && <td className="text-muted">{etiquetaTipo(obtenerTipo(item))}</td>}
                  {etiquetaCantidad && (
                    <td>
                      <span className={style.chip}>{formatoCantidad(obtenerCantidad(item))}</span>
                    </td>
                  )}
                  <td className={style.acciones}>
                    {esFijo(item) ? (
                      <span className="badge text-bg-secondary">Opción fija</span>
                    ) : (
                      <>
                        <BotonEditar
                          etiqueta={`Editar ${obtenerValor(item)}`}
                          onClick={() => iniciarEdicion(item)}
                        />
                        <BotonEliminar
                          etiqueta={`Eliminar ${obtenerValor(item)}`}
                          onClick={() => confirmarEliminar(item)}
                        />
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default CatalogoComponent;
